import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { IError, JsonEditorComponent, JsonEditorOptions } from 'ang-jsoneditor';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { InstanceDataService } from 'src/app/services/instance-data.service';
import { Subscription } from 'rxjs';
import { ProjectType, ProjectTypeStatus, SchemaRevision, SchemaVersion } from 'src/app/Modules/management/models/instance/project-type.model';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { UniqueNameValidation } from 'src/app/Modules/management/core/Validators/unique-name.validator';
import { CustomValidatorService } from 'src/app/services/custom-validator.service';

@Component({
    selector: 'app-view-project-type-modal',
    templateUrl: './view-project-type-modal.component.html',
    styleUrls: ['./view-project-type-modal.component.scss'],
})
export class ViewProjectTypeModalComponent implements OnInit {
    @Input() modalMode: string;
    @Input() projectType: ProjectType;
    @Input() projectTypeSchema: string;
    @Input() projectTypeNameList: string[];
    @Input() defaultProjectStatusTypes: ProjectTypeStatus[];
    @ViewChild('editor') editor: JsonEditorComponent;
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

    tooltip_position: 'above';
    editorOptions: JsonEditorOptions;
    projectTypeName: string;
    pageSize = 4;
    pageEvent: PageEvent;
    currentPageItemLength: number;
    iconUrl = '../../../../../../assets/media/logos/leaks-01.png';
    updatedSchema: string;
    currentInstanceName: string;
    currentAssetType: string;
    instanceNameSub: Subscription;
    assetTypeSub: Subscription;
    changedColorList: ProjectTypeStatus[];
    projectTypeForm: FormGroup;
    projectTypeVersionList: SchemaVersion[];
    displayVersionList: SchemaVersion[];
    isVersionAdd: boolean;

    constructor(
        public ngbActiveModal: NgbActiveModal,
        private cd: ChangeDetectorRef,
        private instanceDataService: InstanceDataService,
        private formBuilder: FormBuilder,
        private customValidator: CustomValidatorService
    ) {
        this.tooltip_position = 'above';
        this.projectTypeVersionList = [];
        this.displayVersionList = [];
    }

    ngOnInit(): void {
        if (this.projectType) {
            this.projectTypeNameList = this.projectTypeNameList.filter((res) => res !== this.projectType.name);
        }
        this.projectTypeForm = this.formBuilder.group({
            name: new FormControl(this.projectType?.name || '', [Validators.required, UniqueNameValidation(this.projectTypeNameList)]),
            description: new FormControl(this.projectType?.description || '', Validators.required),
        });

        if (this.projectType?.schemaVersions) {
            this.projectType.schemaVersions.forEach((version) => {
                this.projectTypeVersionList.push({
                    schemaVersionId: version.schemaVersionId,
                    version: version.version,
                    schema: JSON.parse(version.schema),
                });
            });
        }

        this.currentPageItemLength = this.projectTypeVersionList.length;
        this.displayVersionList = this.projectTypeVersionList.slice().reverse().slice(0, 4);

        this.instanceNameSub = this.instanceDataService.currentInstanceName.subscribe((instanceName: string) => {
            this.currentInstanceName = instanceName;
        });
        this.assetTypeSub = this.instanceDataService.currentAssetType.subscribe((assetType: string) => {
            this.currentAssetType = assetType;
        });
    }

    // function to close the modal without result
    closeModalWithCancel() {
        this.ngbActiveModal.close(false);
    }

    /**
     * * NEW CODE
     */
    // * Save button click
    saveActivityPackType() {
        if (this.modalMode == 'edit') {
            this.projectType.name = this.projectTypeForm.value.name;
            this.projectType.description = this.projectTypeForm.value.description;
            if (this.isVersionAdd) {
                const schemaRevision = new SchemaRevision();
                schemaRevision.schema = typeof this.updatedSchema == 'object' ? JSON.stringify(this.updatedSchema) : this.updatedSchema;
                this.projectType.schemaRevision = schemaRevision;
            }
            this.ngbActiveModal.close(this.projectType);
        } else if (this.modalMode == 'add') {
            const newProjectType = new ProjectType();
            newProjectType.name = this.projectTypeForm.value.name;
            newProjectType.description = this.projectTypeForm.value.description;
            newProjectType.isSelected = false;
            if (this.isVersionAdd) {
                const schemaRevision = new SchemaRevision();
                schemaRevision.schema = typeof this.updatedSchema == 'object' ? JSON.stringify(this.updatedSchema) : this.updatedSchema;
                newProjectType.schemaRevision = schemaRevision;
            }
            this.ngbActiveModal.close(newProjectType);
        }
    }

    // * method to add new version
    addNewVersion(): void {
        this.isVersionAdd = true;
        const emptyObj = {};
        this.projectTypeForm.markAsDirty();
        if (this.modalMode === 'edit') {
            //  * while editing the activity pack type
            const version = new SchemaVersion();
            if (this.projectType?.schemaVersions?.length > 0) {
                version.schema = JSON.parse(this.projectType.schemaVersions[this.projectType.schemaVersions.length - 1].schema);
            } else {
                version.schema = JSON.parse(JSON.stringify(emptyObj));
            }
            version.version = this.projectTypeVersionList.length + 1;
            this.projectTypeVersionList = [...this.projectTypeVersionList, version];
            this.updatedSchema = version.schema;
        } else if (this.modalMode === 'add') {
            // * while creating the activity pack type
            const version = new SchemaVersion();
            version.schema = JSON.parse(JSON.stringify(emptyObj));
            version.version = this.projectTypeVersionList.length + 1;
            this.projectTypeVersionList = [...this.projectTypeVersionList, version];
            this.updatedSchema = version.schema;
        }

        if (this.projectTypeVersionList.length > 4) {
            this.currentPageItemLength = this.projectTypeVersionList.length;
            this.cd.detectChanges();
            this.paginator.firstPage();
        }

        this.displayVersionList = this.projectTypeVersionList.slice().reverse().slice(0, 4);
    }

    // * helper method to get the schema data
    getSchemaData(schemaVersionId: string) {
        if (schemaVersionId) {
            const schema = this.projectTypeVersionList.find((schema) => schema.schemaVersionId === schemaVersionId);
            return schema.schema;
        } else {
            return this.projectTypeVersionList[this.projectTypeVersionList.length - 1].schema;
        }
    }

    // * helper method to return the custom json-editor options
    getJsonEditorOptions(schemaVersion: SchemaVersion) {
        this.editorOptions = new JsonEditorOptions();
        this.editorOptions.modes = ['tree', 'view', 'code', 'text'];

        // ✅ Enable necessary UI components
        this.editorOptions.mainMenuBar = true; // Enables the menu bar
        this.editorOptions.navigationBar = true; // Enables the navigation bar
        this.editorOptions.statusBar = true; // Enables the status bar
        if (schemaVersion.schemaVersionId) {
            // * return json options with validation not to edit the the schema
            this.editorOptions.onEditable = () => {
                return false;
            };
        } else {
            // * return json options with  allow to edit the the schema
            this.editorOptions.onEditable = () => {
                return true;
            };

            // * getting the json schema
            this.editorOptions.onChangeText = (event) => {
                this.updatedSchema = event;
                try {
                    const parsedSchema = JSON.parse(event);

                    if (!this.editor.isValidJson() || Object.keys(parsedSchema).length === 0) {
                        this.projectTypeForm.setErrors({
                            inValidSchema: 'Schema is not valid',
                        });
                    } else {
                        this.projectTypeForm.setErrors(null);
                    }
                } catch (error) {
                    this.projectTypeForm.setErrors({
                        inValidSchema: 'Schema is not valid',
                    });
                }
            };
        }
        return this.editorOptions;
    }

    // * pagination handler
    getPaginationData(event: PageEvent) {
        this.displayVersionList = this.projectTypeVersionList
            .slice()
            .reverse()
            .slice(event.pageIndex * event.pageSize, event.pageIndex * event.pageSize + event.pageSize);
        return event;
    }
    checkDisable() {
        if (this.modalMode == 'add') {
            return !(this.projectTypeForm.valid && this.projectTypeForm.dirty && this.isSchemaValid());
        } else {
            return !(this.projectTypeForm.valid && this.projectTypeForm.dirty);
        }
    }
    isSchemaValid(): boolean {
        try {
            const parsedSchema = JSON.parse(this.updatedSchema);

            if ((!this.updatedSchema && !this.editor.isValidJson()) || Object.keys(parsedSchema).length === 0) {
                return false;
            } else {
                return true;
            }
        } catch (error) {
            return false;
        }
    }

    onKeyPress(event: KeyboardEvent) {
        this.customValidator.validateKeyPress(event);
    }
}
